import { Article } from '../../components/Layout'
import { H1, H2, H3, P, Lead, UL, LI, Callout, CodeBlock, Code, Steps, Step } from '../../components/DocComponents'

const toc = [
  { id: 'how-it-works', title: 'How tracing works',    level: 2 as const },
  { id: 'jaeger',       title: 'Connecting Jaeger',    level: 2 as const },
  { id: 'tempo',        title: 'Grafana Tempo',         level: 2 as const },
  { id: 'datadog',      title: 'DataDog APM',           level: 2 as const },
  { id: 'newrelic',     title: 'New Relic',             level: 2 as const },
  { id: 'log-trace',    title: 'Log-to-trace correlation', level: 2 as const },
  { id: 'env-vars',     title: 'Environment variables', level: 2 as const },
]

export function Tracing() {
  return (
    <Article toc={toc}>
      <H1>Distributed Tracing</H1>
      <Lead>
        Every Rabbit service is instrumented with the OpenTelemetry SDK. Traces propagate across
        service boundaries via the W3C <Code>traceparent</Code> header, giving you end-to-end
        visibility into any request that spans multiple services.
      </Lead>

      <H2 id="how-it-works">How tracing works</H2>
      <P>
        Each Node.js service loads <Code>src/tracing.ts</Code> via <Code>--require</Code> before
        any application code, ensuring the OpenTelemetry SDK patches all instrumentable modules
        (Express, pg, http, grpc) at startup. Lens (Python) uses
        <Code>FastAPIInstrumentor</Code> which instruments all FastAPI routes automatically.
      </P>
      <P>
        Auto-instrumentation captures:
      </P>
      <UL>
        <LI><strong>Inbound HTTP</strong> — every request becomes a root span with method, route, status code, and latency</LI>
        <LI><strong>Outbound HTTP</strong> — calls from one service to another (e.g. brainer → auth-service validate) appear as child spans</LI>
        <LI><strong>PostgreSQL queries</strong> — every <Code>database.query()</Code> call is a child span with the SQL statement and row count</LI>
        <LI><strong>MySQL queries</strong> — brainer's secondary MySQL connection is similarly instrumented</LI>
        <LI><strong>gRPC</strong> — buzz's gRPC server and any gRPC clients are auto-instrumented</LI>
      </UL>
      <Callout type="info">
        The OTel SDK sends spans asynchronously in the background. There is no synchronous
        overhead on the request path beyond recording start/end times.
      </Callout>

      <H2 id="jaeger">Connecting Jaeger</H2>
      <P>
        Jaeger all-in-one accepts OTLP over HTTP (port 4318) and gRPC (port 4317). Set the
        endpoint on each service:
      </P>
      <CodeBlock language="bash" title=".env — point to your Jaeger instance">
{`OTEL_EXPORTER_OTLP_ENDPOINT=http://your-jaeger-host:4318`}
      </CodeBlock>
      <P>
        The default value is <Code>http://localhost:4318</Code>, which works with the bundled
        <Code>docker-compose.observability.yml</Code>. In production, set this to your
        Jaeger collector or OTEL Collector URL.
      </P>
      <CodeBlock language="bash" title="Start Jaeger locally">
{`docker compose -f docker-compose.observability.yml up jaeger -d

# Open the Jaeger UI
open http://localhost:16686`}
      </CodeBlock>
      <P>
        In the Jaeger UI, select a service from the dropdown (e.g. <Code>brainer</Code>) and
        click <strong>Find Traces</strong>. Click any trace to expand it into a waterfall view
        showing all spans across services.
      </P>

      <H2 id="tempo">Grafana Tempo</H2>
      <P>
        Grafana Tempo is a cost-efficient alternative to Jaeger that integrates natively with
        Grafana dashboards. Configure the OTLP endpoint to point to your Tempo instance:
      </P>
      <CodeBlock language="bash" title=".env — point to Tempo">
{`OTEL_EXPORTER_OTLP_ENDPOINT=http://your-tempo-host:4318`}
      </CodeBlock>
      <CodeBlock language="yaml" title="Tempo datasource in Grafana (datasources.yml)">
{`- name: Tempo
  type: tempo
  access: proxy
  url: http://tempo:3200
  jsonData:
    tracesToLogsV2:
      datasourceUid: loki
      spanStartTimeShift: '-1h'
      spanEndTimeShift: '1h'
      filterByTraceID: true
      customQuery: true
      query: '{job="\${__span.tags.job}"} |= "\${__trace.traceId}"'`}
      </CodeBlock>

      <H2 id="datadog">DataDog APM</H2>
      <P>
        DataDog supports OTLP ingestion via the DataDog Agent. Enable the OTLP receiver in your
        agent configuration:
      </P>
      <CodeBlock language="yaml" title="datadog.yaml — enable OTLP receiver">
{`otlp_config:
  receiver:
    protocols:
      http:
        endpoint: 0.0.0.0:4318`}
      </CodeBlock>
      <CodeBlock language="bash" title=".env — point services to the DataDog Agent">
{`OTEL_EXPORTER_OTLP_ENDPOINT=http://your-datadog-agent:4318`}
      </CodeBlock>
      <P>
        Traces appear in DataDog APM under the service names set by
        <Code>OTEL_SERVICE_NAME</Code> (defaults: <Code>auth-service</Code>, <Code>brainer</Code>, etc.).
      </P>

      <H2 id="newrelic">New Relic</H2>
      <P>
        New Relic's OTLP endpoint accepts traces directly — no agent installation required:
      </P>
      <CodeBlock language="bash" title=".env — send traces to New Relic">
{`OTEL_EXPORTER_OTLP_ENDPOINT=https://otlp.nr-data.net:4318
OTEL_EXPORTER_OTLP_HEADERS=api-key=<YOUR_NEW_RELIC_LICENSE_KEY>`}
      </CodeBlock>
      <Callout type="warning">
        The <Code>OTEL_EXPORTER_OTLP_HEADERS</Code> env var is not yet wired by default —
        you would need to pass it to the <Code>OTLPTraceExporter</Code> constructor in
        <Code>src/tracing.ts</Code> of each service:
        <Code>{'headers: { "api-key": process.env.NR_LICENSE_KEY }'}</Code>
      </Callout>

      <H2 id="log-trace">Log-to-trace correlation</H2>
      <P>
        Every Winston log line (Node.js) and every Python log record includes a
        <Code>traceId</Code> field when emitted inside an active span:
      </P>
      <CodeBlock language="json" title="Example structured log line">
{`{
  "timestamp": "2026-05-31T10:00:00.000Z",
  "level": "info",
  "message": "Drinker run completed",
  "traceId": "a4c2e1b8d3f7a9c0e5b2d4f6a8c1e3b5",
  "spanId":  "b1c2d3e4f5a6b7c8",
  "drinkerId": "uuid-...",
  "durationMs": 142
}`}
      </CodeBlock>
      <P>
        In Grafana, configure a <strong>Derived Field</strong> on your log datasource (Loki) to
        create a clickable link from <Code>traceId</Code> to Jaeger or Tempo:
      </P>
      <CodeBlock language="yaml" title="Loki datasource derived field (Grafana UI → Datasource → Derived Fields)">
{`Name:        TraceID
Regex:       "traceId":"([a-f0-9]+)"
Query:       \${__value.raw}
Internal link: Jaeger (or Tempo)`}
      </CodeBlock>

      <H2 id="env-vars">Environment variables</H2>
      <CodeBlock language="bash" title="Tracing environment variables (all services)">
{`# Service name shown in Jaeger / Tempo / DataDog
# Default: service-specific (auth-service, brainer, etc.)
OTEL_SERVICE_NAME=auth-service

# OTLP HTTP endpoint for the trace exporter
# Default: http://localhost:4318
# Append nothing — /v1/traces is appended automatically
OTEL_EXPORTER_OTLP_ENDPOINT=http://localhost:4318`}
      </CodeBlock>
    </Article>
  )
}
